import { catalogEndpoints } from '@/core/api/endpoints'
import { sessionStore } from '@/core/auth/SessionStore'
import { db } from '@/core/db/dexie'
import {
  equipeEncarregadoDtoToEntity,
  localCargaDtoToEntity,
  localDescargaDtoToEntityRod,
  localDescargaDtoToEntityUrb,
  materialDtoToEntity,
  mveDtoToEntity,
  namedToDiametro,
  namedToEquipe,
  namedToMaterialRede,
  namedToTipoRede,
  namedWithChapaToApontador,
  namedWithChapaToEncarregado,
  namedWithChapaToMotorista,
  obraDtoToEntity,
  projetoDtoToEntity,
  projetoServicoDtoToEntity,
  servicoDtoToEntity,
  trechoDtoToEntity,
  unidadeDtoToEntity,
} from '@/core/db/mappers'

/**
 * Sync engine de catálogos. Substitui `CatalogRepository.refreshAll()` do Android.
 *
 * Estratégia:
 *  - Cada catálogo é uma `Promise.allSettled` separada — falha isolada (1 endpoint down não
 *    quebra os outros 17).
 *  - ETag tracking via `cat_meta` (If-None-Match → 304 = não toca o store local).
 *  - Mutex global para evitar 2 syncs simultâneos (Background + foreground racing).
 */

export interface RefreshSummary {
  ok: string[]
  failed: Record<string, string>
  durationMs: number
}

let runningPromise: Promise<RefreshSummary> | null = null

export async function refreshAllCatalogs(): Promise<RefreshSummary> {
  // Mutex: se já tem refresh rodando, retorna a mesma promise
  if (runningPromise) return runningPromise
  runningPromise = doRefresh().finally(() => {
    runningPromise = null
  })
  return runningPromise
}

async function doRefresh(): Promise<RefreshSummary> {
  const t0 = performance.now()
  const ok: string[] = []
  const failed: Record<string, string> = {}

  async function step<T>(name: string, fn: () => Promise<T>) {
    try {
      await fn()
      ok.push(name)
    } catch (e) {
      failed[name] = e instanceof Error ? e.message : String(e)
    }
  }

  // Dispara todos em paralelo (Promise.allSettled implícito via step()).
  await Promise.all([
    step('projetos', refreshProjetos),
    step('obras', refreshObras),
    step('servicos', refreshServicos),
    step('encarregados', refreshEncarregados),
    step('motoristas', refreshMotoristas),
    step('apontadores', refreshApontadores),
    step('equipes', refreshEquipes),
    step('unidades', refreshUnidades),
    step('materiais', refreshMateriais),
    step('materiais_rede', async () => {
      for (const pid of collectProjetoIds()) {
        await refreshMateriaisRedePorProjeto(pid)
      }
    }),
    step('diametros', async () => {
      for (const pid of collectProjetoIds()) {
        await refreshDiametrosPorProjeto(pid)
      }
    }),
    step('tipos_rede', async () => {
      for (const pid of collectProjetoIds()) {
        await refreshTiposRedePorProjeto(pid)
      }
    }),
    step('trechos', refreshTrechos),
    step('mves', refreshMves),
    step('locais_carga', async () => {
      // Locais de carga são escopados por projeto. Inclui:
      //  - Todos os projetos explícitos da sessão (operador comum)
      //  - O `projetoAtualId` selecionado no home (admin com projetos=[] precisa
      //    desse fallback, senão a query in:[] não devolve nada).
      // Bug fix 2026-05-26: usuário admin no projeto 161 (com 224 locais
      // cadastrados) via picker de Origem vazio porque projetos=[] no JWT.
      for (const pid of collectProjetoIds()) {
        await refreshLocaisCargaPorProjeto(pid)
      }
    }),
    step('locais_descarga_rod', refreshLocaisDescargaRod),
    step('locais_descarga_urb', refreshLocaisDescargaUrb),
    step('projeto_servico', refreshProjetoServico),
    step('equipe_encarregado', refreshEquipeEncarregado),
  ])

  return { ok, failed, durationMs: performance.now() - t0 }
}

// =============================================================================
//  Helpers
// =============================================================================

/**
 * IDs de projetos que precisam de sync escopado (catálogos por-projeto).
 * Inclui os projetos explícitos no JWT da sessão + `projetoAtualId` selecionado
 * no home (cobre usuário admin com `projetos=[]` que precisa do fallback).
 */
function collectProjetoIds(): number[] {
  const session = sessionStore.current()
  const ids = new Set<number>(session?.projetos ?? [])
  if (session?.projetoAtualId != null) ids.add(session.projetoAtualId)
  return Array.from(ids)
}

async function getEtag(name: string): Promise<string | undefined> {
  const m = await db.cat_meta.get(name)
  return m?.version
}

async function saveEtag(name: string, version: string) {
  await db.cat_meta.put({ catalogName: name, version, fetchedAt: Date.now() })
}

// =============================================================================
//  Refreshers individuais (mantêm o nome usado em CatMeta para o ETag)
// =============================================================================

/**
 * IMPORTANTE: cada refresh faz `clear()+bulkPut` dentro de uma TRANSAÇÃO Dexie.
 * Sem transação, useLiveQuery vê 2 mudanças (esvazia → preenche) e re-renderiza com
 * o estado intermediário "tabela vazia", o que faz `useProjeto()` retornar undefined
 * por algumas dezenas de ms. Componentes que guardam `if (projeto == null) <Navigate/>`
 * acabam expulsos da tela. Em transação, o consumer só vê o estado final.
 */

export async function refreshProjetos() {
  const etag = await getEtag('projetos')
  const res = await catalogEndpoints.projetos(etag)
  if (!res.data) return // 304
  const items = res.data.items.map(projetoDtoToEntity)
  await db.transaction('rw', db.cat_projeto, async () => {
    await db.cat_projeto.clear()
    await db.cat_projeto.bulkPut(items)
  })
  if (res.etag) await saveEtag('projetos', res.etag)
}

export async function refreshObras() {
  const etag = await getEtag('obras')
  const res = await catalogEndpoints.obras(etag)
  if (!res.data) return
  const items = res.data.items.map(obraDtoToEntity)
  await db.transaction('rw', db.cat_obra, async () => {
    await db.cat_obra.clear()
    await db.cat_obra.bulkPut(items)
  })
  if (res.etag) await saveEtag('obras', res.etag)
}

export async function refreshServicos() {
  const etag = await getEtag('servicos')
  const res = await catalogEndpoints.servicos(etag)
  if (!res.data) return
  const items = res.data.items.map(servicoDtoToEntity)
  await db.transaction('rw', db.cat_servico, async () => {
    await db.cat_servico.clear()
    await db.cat_servico.bulkPut(items)
  })
  if (res.etag) await saveEtag('servicos', res.etag)
}

export async function refreshEncarregados() {
  const etag = await getEtag('encarregados')
  const res = await catalogEndpoints.encarregados(etag)
  if (!res.data) return
  const items = res.data.items.map(namedWithChapaToEncarregado)
  await db.transaction('rw', db.cat_encarregado, async () => {
    await db.cat_encarregado.clear()
    await db.cat_encarregado.bulkPut(items)
  })
  if (res.etag) await saveEtag('encarregados', res.etag)
}

export async function refreshMotoristas() {
  const etag = await getEtag('motoristas')
  const res = await catalogEndpoints.motoristas(etag)
  if (!res.data) return
  const items = res.data.items.map(namedWithChapaToMotorista)
  await db.transaction('rw', db.cat_motorista, async () => {
    await db.cat_motorista.clear()
    await db.cat_motorista.bulkPut(items)
  })
  if (res.etag) await saveEtag('motoristas', res.etag)
}

export async function refreshApontadores() {
  const etag = await getEtag('apontadores')
  const res = await catalogEndpoints.apontadores(etag)
  if (!res.data) return
  const items = res.data.items.map(namedWithChapaToApontador)
  await db.transaction('rw', db.cat_apontador, async () => {
    await db.cat_apontador.clear()
    await db.cat_apontador.bulkPut(items)
  })
  if (res.etag) await saveEtag('apontadores', res.etag)
}

export async function refreshEquipes() {
  const etag = await getEtag('equipes')
  const res = await catalogEndpoints.equipes(etag)
  if (!res.data) return
  const items = res.data.items.map(namedToEquipe)
  await db.transaction('rw', db.cat_equipe, async () => {
    await db.cat_equipe.clear()
    await db.cat_equipe.bulkPut(items)
  })
  if (res.etag) await saveEtag('equipes', res.etag)
}

export async function refreshUnidades() {
  const etag = await getEtag('unidades')
  const res = await catalogEndpoints.unidades(etag)
  if (!res.data) return
  const items = res.data.items.map(unidadeDtoToEntity)
  await db.transaction('rw', db.cat_unidade, async () => {
    await db.cat_unidade.clear()
    await db.cat_unidade.bulkPut(items)
  })
  if (res.etag) await saveEtag('unidades', res.etag)
}

export async function refreshMateriais() {
  const etag = await getEtag('materiais')
  const res = await catalogEndpoints.materiais(etag)
  if (!res.data) return
  const items = res.data.items.map(materialDtoToEntity)
  await db.transaction('rw', db.cat_material, async () => {
    await db.cat_material.clear()
    await db.cat_material.bulkPut(items)
  })
  if (res.etag) await saveEtag('materiais', res.etag)
}

/**
 * Catálogos de rede (material rede, diâmetro, tipo rede) ficaram escopados
 * por projeto a partir de 2026-05-27. Cada projeto vira uma entrada própria
 * em cat_meta (ex: "materiais_rede_<projetoId>"), com merge na tabela local
 * (sem clear global — preserva itens de outros projetos do operador).
 *
 * Quando o usuário tem N projetos vinculados, fazemos N requests por catálogo
 * — mesmo padrão de refreshLocaisCargaPorProjeto.
 */
export async function refreshMateriaisRedePorProjeto(projetoId: number) {
  const key = `materiais_rede_${projetoId}`
  const etag = await getEtag(key)
  const res = await catalogEndpoints.materiaisRedePorProjeto(projetoId, etag)
  if (!res.data) return
  const items = res.data.items.map(namedToMaterialRede).map((m) => ({
    ...m,
    projetoId,
  }))
  // Sem clear global — só remove os itens deste projeto e re-popula.
  // Outros projetos sincronizados ficam intactos.
  await db.transaction('rw', db.cat_material_rede, async () => {
    await db.cat_material_rede.where('projetoId').equals(projetoId).delete()
    await db.cat_material_rede.bulkPut(items)
  })
  if (res.etag) await saveEtag(key, res.etag)
}

export async function refreshDiametrosPorProjeto(projetoId: number) {
  const key = `diametros_${projetoId}`
  const etag = await getEtag(key)
  const res = await catalogEndpoints.diametrosPorProjeto(projetoId, etag)
  if (!res.data) return
  const items = res.data.items.map(namedToDiametro).map((d) => ({
    ...d,
    projetoId,
  }))
  await db.transaction('rw', db.cat_diametro, async () => {
    await db.cat_diametro.where('projetoId').equals(projetoId).delete()
    await db.cat_diametro.bulkPut(items)
  })
  if (res.etag) await saveEtag(key, res.etag)
}

export async function refreshTiposRedePorProjeto(projetoId: number) {
  const key = `tipos_rede_${projetoId}`
  const etag = await getEtag(key)
  const res = await catalogEndpoints.tiposRedePorProjeto(projetoId, etag)
  if (!res.data) return
  const items = res.data.items.map(namedToTipoRede).map((t) => ({
    ...t,
    projetoId,
  }))
  await db.transaction('rw', db.cat_tipo_rede, async () => {
    await db.cat_tipo_rede.where('projetoId').equals(projetoId).delete()
    await db.cat_tipo_rede.bulkPut(items)
  })
  if (res.etag) await saveEtag(key, res.etag)
}

export async function refreshTrechos() {
  const etag = await getEtag('trechos')
  const res = await catalogEndpoints.trechos(etag)
  if (!res.data) return
  const items = res.data.items.map(trechoDtoToEntity)
  await db.transaction('rw', db.cat_trecho, async () => {
    await db.cat_trecho.clear()
    await db.cat_trecho.bulkPut(items)
  })
  if (res.etag) await saveEtag('trechos', res.etag)
}

export async function refreshMves() {
  const etag = await getEtag('mves')
  const res = await catalogEndpoints.mves(etag)
  if (!res.data) return
  const items = res.data.items.map(mveDtoToEntity)
  await db.transaction('rw', db.cat_mve, async () => {
    await db.cat_mve.clear()
    await db.cat_mve.bulkPut(items)
  })
  if (res.etag) await saveEtag('mves', res.etag)
}

export async function refreshLocaisCargaPorProjeto(projetoId: number) {
  const key = `locais_carga_${projetoId}`
  const etag = await getEtag(key)
  const res = await catalogEndpoints.locaisCarga(projetoId, etag)
  if (!res.data) return
  const items = res.data.items.map(localCargaDtoToEntity)
  // Replace só os do projeto (não dá clear global)
  await db.transaction('rw', db.cat_local_carga, async () => {
    await db.cat_local_carga.where('projetoId').equals(projetoId).delete()
    await db.cat_local_carga.bulkPut(items)
  })
  if (res.etag) await saveEtag(key, res.etag)
}

export async function refreshLocaisDescargaRod() {
  const etag = await getEtag('locais_descarga_rod')
  const res = await catalogEndpoints.locaisDescargaRod(etag)
  if (!res.data) return
  const items = res.data.items.map(localDescargaDtoToEntityRod)
  await db.transaction('rw', db.cat_local_descarga_rod, async () => {
    await db.cat_local_descarga_rod.clear()
    await db.cat_local_descarga_rod.bulkPut(items)
  })
  if (res.etag) await saveEtag('locais_descarga_rod', res.etag)
}

export async function refreshLocaisDescargaUrb() {
  const etag = await getEtag('locais_descarga_urb')
  const res = await catalogEndpoints.locaisDescargaUrb(etag)
  if (!res.data) return
  const items = res.data.items.map(localDescargaDtoToEntityUrb)
  await db.transaction('rw', db.cat_local_descarga_urb, async () => {
    await db.cat_local_descarga_urb.clear()
    await db.cat_local_descarga_urb.bulkPut(items)
  })
  if (res.etag) await saveEtag('locais_descarga_urb', res.etag)
}

export async function refreshProjetoServico() {
  const etag = await getEtag('projeto_servico')
  const res = await catalogEndpoints.projetoServico(etag)
  if (!res.data) return
  const items = res.data.items.map(projetoServicoDtoToEntity)
  await db.transaction('rw', db.cat_projeto_servico, async () => {
    await db.cat_projeto_servico.clear()
    await db.cat_projeto_servico.bulkPut(items)
  })
  if (res.etag) await saveEtag('projeto_servico', res.etag)
}

export async function refreshEquipeEncarregado() {
  const etag = await getEtag('equipe_encarregado')
  const res = await catalogEndpoints.equipeEncarregado(etag)
  if (!res.data) return
  const items = res.data.items.map(equipeEncarregadoDtoToEntity)
  await db.transaction('rw', db.cat_equipe_encarregado, async () => {
    await db.cat_equipe_encarregado.clear()
    await db.cat_equipe_encarregado.bulkPut(items)
  })
  if (res.etag) await saveEtag('equipe_encarregado', res.etag)
}
